import { useEffect } from 'react';
import { useEventCallback } from './useEventCallback';

/**
 * Отслеживает попадание элемента в видимую область экрана.
 * Например используется для загрузки изобжарений в слайдерах и пр.
 * Техника направлена на экономию трафика и как следствие более быструю изначальную загрузку первой видимой области сайта.
 */
export const useIntersection = <T extends HTMLElement = HTMLElement>(
  element: React.RefObject<T> | undefined,
  callback: () => void = () => {},
) => {
  const cbRef = useEventCallback(callback);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry) {
                if (entry.isIntersecting || entry.intersectionRatio > 0) {
                  cbRef();
                }
              }
            });
          },
          {
            rootMargin: '100px',
            threshold: 0.15,
          },
        );

        if (element?.current) {
          observer.observe(element.current);

          return () => {
            if (element.current) {
              // eslint-disable-next-line react-hooks/exhaustive-deps
              observer?.unobserve(element?.current);
            }
          };
        }
      }
    } catch (e) {
      console.error('Unhandled exception in useIntersection', e);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
